/**
 * Tool-name → visitor-facing "thinking" label. Returned by
 * `labelForTool(name)`; null means the tool is internal and we don't
 * surface anything for it.
 *
 * Two buckets:
 *   - Visible work: searching content, navigating, opening content, etc.
 *     The visitor sees a status line while the agent is doing it.
 *   - Silent work: memory writes (set_belief, record_intent_signal, …),
 *     emitters (emit_message, emit_suggestions, …) — these are plumbing,
 *     not actions the visitor should think about.
 *
 * Server-side tool names land here both in camelCase (Anthropic tool
 * loop) and snake_case (Pipecat / voice path). The lookup normalizes.
 */

const LABELS: Record<string, string> = {
  // Content + RAG
  searchAvailableContent: 'Searching the knowledge base…',
  emitRecommendations: 'Finding something to show you…',

  // Page actions
  openContent: 'Pulling that up for you…',
  contentSeek: 'Taking you there…',
  navigateToPage: 'Heading to a relevant page…',
  highlightElement: 'Pointing this out on the page…',

  // Meeting
  bookMeeting: 'Setting up the booking link…',
};

/**
 * Lookup label for a server-emitted tool name. Returns null when the
 * tool is internal (memory, emitters) so the caller skips showing
 * anything. Accepts both camelCase and snake_case spellings.
 */
export function labelForTool(name: string): string | null {
  if (LABELS[name]) return LABELS[name];
  // Normalize snake_case → camelCase for voice-path callers.
  const camel = name.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return LABELS[camel] ?? null;
}
